import FocusTrap from 'focus-trap-react';
import noop from 'lodash/noop';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { CSSTransition } from 'react-transition-group';

import CloseX from 'components/icons/navigation/CloseX';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import * as AdaChat from 'utilities/adaChat';
import * as Types from 'utilities/types';

const toggleBodyScroll = (show, isSlideoutAlreadyOpen) => {
  if (show) {
    document.body.classList.add('overflow-hidden');
  } else if (!isSlideoutAlreadyOpen) {
    document.body.classList.remove('overflow-hidden');
  }
};

export const WIDTH_840 = 'w-840';
export const WIDTH_720 = 'w-720';
export const WIDTH_500 = 'w-500';
export const WIDTH_FULL = 'w-full';

const Slideout = ({
  backPath = '',
  children,
  closeButtonClassname = '',
  closeCallback = () => {},
  closeClassname = '',
  customOverlayCloseCallback,
  desktopWidth = WIDTH_720,
  headerElement,
  hideTopBar = false,
  id,
  isSlideoutAlreadyOpen = false,
  keyboardAvoidingFooter = false,
  labelId,
  navigate = () => {},
  renderCloseFirst = false,
  scrollableRef,
  show,
  slideoutContainerClassname = '',
  topBarClassname = '',
  transitionClassname = 'slideout',
  unmountSlideout = noop,
  updateAdaChat = true,
}) => {
  const { isLargeDesktopView } = useScreenSizeConstants();
  const [lastFocusedEl, setLastFocusedEl] = useState(null);

  const modalRef = scrollableRef || useRef();
  const overlayRef = useRef();
  // Keep track of if we're registering listeners, so that when the clean up
  // function is triggered we unregister any set listeners.
  const addedListenerRef = useRef(false);
  const closeCallbackRef = useRef(closeCallback);
  closeCallbackRef.current = closeCallback;

  const slideoutBackgroundClassName = `slideout-background ${isSlideoutAlreadyOpen ? '' : 'slideout-background-color'}`;
  const focusOnLastEl = () => {
    if (lastFocusedEl) setTimeout(() => lastFocusedEl.focus());
  };

  // Setting timeouts for event listeners to allow time for elements to receive/track focus
  const addKeyDownListener = () =>
    setTimeout(() => document.addEventListener('keydown', checkDocumentKeyDown));

  const removeKeyDownListener = () =>
    setTimeout(() => document.removeEventListener('keydown', checkDocumentKeyDown));

  const checkDocumentKeyDown = event => {
    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      navigateBack();
    }
  };

  const updateAdaChatPosition = () =>
    AdaChat.updateAdaChatPosition({
      desktopWidth,
      keyboardAvoidingFooter,
      show,
      isMobileOrTablet: !isLargeDesktopView,
    });

  const addWindowResizeListener = listener => window.addEventListener('resize', listener);

  const removeWindowResizeListener = listener => window.removeEventListener('resize', listener);

  useEffect(() => {
    if (!isSlideoutAlreadyOpen) {
      if (show) {
        setLastFocusedEl(document.activeElement);
        addKeyDownListener();
        if (updateAdaChat) {
          addWindowResizeListener(updateAdaChatPosition);
          AdaChat.updateAdaChatPosition({
            desktopWidth,
            keyboardAvoidingFooter,
            show,
            isMobileOrTablet: !isLargeDesktopView,
          });
        }

        addedListenerRef.current = true;
      }

      return () => {
        if (addedListenerRef.current) {
          removeKeyDownListener();
          if (updateAdaChat) {
            removeWindowResizeListener(updateAdaChatPosition);
            AdaChat.resetPosition();
          }
          addedListenerRef.current = false;
        }
      };
    }
  }, [show, isSlideoutAlreadyOpen]);

  const onClose = (isFromOverlay = false) => {
    const isFromOverlayCheck = typeof isFromOverlay === 'boolean' ? isFromOverlay : false;
    if (isFromOverlayCheck && isSlideoutAlreadyOpen && customOverlayCloseCallback) {
      customOverlayCloseCallback();
    } else {
      closeCallbackRef.current();
    }
    focusOnLastEl();
  };

  const navigateBack = () => {
    onClose();
    if (backPath) navigate(backPath);
  };

  const renderDefaultHeader = () => {
    const textClassNames =
      'appearance-none border-none text-charcoal-100 hover:text-charcoal-80 focus:text-charcoal-80 bg-transparent';
    if (backPath)
      return (
        <Link
          to={backPath}
          onClick={onClose}
          className={`${textClassNames} ${closeButtonClassname}`}
          aria-label="Go back in slideout"
        >
          <CloseX className={`h-8 w-8 ${closeClassname}`} />
        </Link>
      );
    return (
      <button
        className={`${textClassNames} ${closeButtonClassname}`}
        onClick={onClose}
        aria-label="Close slideout"
      >
        <CloseX className={`h-8 w-8 ${closeClassname}`} />
      </button>
    );
  };

  const renderTopBar = () => {
    if (hideTopBar) return null;
    return (
      <nav
        className={`sticky top-0 z-two flex items-center justify-end border-b border-solid border-charcoal-60 bg-white-100 px-6 py-4 ${topBarClassname}`}
      >
        {headerElement}
        {renderDefaultHeader()}
      </nav>
    );
  };

  const renderTopBarCloseFirst = () => (
    <nav
      className={`sticky top-0 z-two grid grid-cols-3 items-center border-b border-solid border-charcoal-60 bg-white-100 px-4 py-4 ${topBarClassname}`}
    >
      {renderDefaultHeader()}
      {headerElement}
    </nav>
  );

  return (
    <FocusTrap
      active={show}
      focusTrapOptions={{
        onActivate: () => toggleBodyScroll(true),
        onDeactivate: () => toggleBodyScroll(false, isSlideoutAlreadyOpen),
        returnFocusOnDeactivate: false,
        fallbackFocus: `#${id}`,
      }}
    >
      <div
        id={id}
        aria-labelledby={labelId}
        role="dialog"
        aria-hidden={!show}
        className="react-modal"
        data-testid={id}
      >
        <CSSTransition
          nodeRef={modalRef}
          key="drawerSlideout"
          in={show}
          timeout={200}
          classNames={transitionClassname}
          unmountOnExit
          appear
          onExited={unmountSlideout}
        >
          <section
            className={`slideout-container ${slideoutContainerClassname} ${`slideout-${desktopWidth}`}`}
            ref={modalRef}
          >
            {renderCloseFirst ? renderTopBarCloseFirst() : renderTopBar()}
            {children}
          </section>
        </CSSTransition>
        <CSSTransition
          nodeRef={overlayRef}
          key="overlaySlideout"
          in={show}
          timeout={200}
          classNames="fadeInOut"
          unmountOnExit
          appear
        >
          {backPath ? (
            <Link
              ref={overlayRef}
              className={slideoutBackgroundClassName}
              to={backPath}
              tabIndex="0"
              onClick={() => onClose(true)}
            >
              <span>Go to previous slideout page</span>
            </Link>
          ) : (
            <button
              ref={overlayRef}
              className={slideoutBackgroundClassName}
              aria-label="Click dimmed background to close slideout"
              tabIndex="0"
              onClick={() => onClose(true)}
            >
              <span>Close slideout</span>
            </button>
          )}
        </CSSTransition>
      </div>
    </FocusTrap>
  );
};

Slideout.propTypes = {
  backPath: PropTypes.string,
  children: PropTypes.node,
  closeButtonClassname: PropTypes.string,
  closeCallback: PropTypes.func,
  closeClassname: PropTypes.string,
  customOverlayCloseCallback: PropTypes.func,
  desktopWidth: PropTypes.oneOf([WIDTH_720, WIDTH_500, WIDTH_840, WIDTH_FULL]),
  headerElement: PropTypes.node,
  hideTopBar: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isSlideoutAlreadyOpen: PropTypes.bool,
  keyboardAvoidingFooter: PropTypes.bool,
  labelId: PropTypes.string.isRequired,
  renderCloseFirst: PropTypes.bool,
  scrollableRef: Types.refType,
  show: PropTypes.bool.isRequired,
  slideoutContainerClassname: PropTypes.string,
  topBarClassname: PropTypes.string,
  transitionClassname: PropTypes.string,
  unmountSlideout: PropTypes.func,
  updateAdaChat: PropTypes.bool,
  navigate: PropTypes.func,
};

export default Slideout;
